/**
 * Quotes filter bar. Same three-tier structure as `BookingsFilterBar`, with the
 * differences the schema forces:
 *
 *   - only two service types (booking_quotes.service_type CHECK)
 *   - no payment-status or balance-due filter (columns don't exist)
 *   - no "include test" toggle (booking_quotes has no is_test column) — omitted
 *     rather than rendered as a control that does nothing
 *   - quote-specific flags: operator-priced, flat rate, has discount
 *   - NO attribution warning: utm_* are local columns here, so no join is
 *     involved and no rows are excluded by filtering on them
 */

import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Search, SlidersHorizontal } from 'lucide-react'
import {
  QUOTE_SERVICE_TYPE_OPTIONS,
  QUOTE_STATUS_OPTIONS,
  countActiveFilters,
  type QuoteFilters,
} from '@limo/shared'
import { useAccountVehicles, useUtmOptions } from '@/hooks/useReports'
import { MultiSelectFilter } from './MultiSelectFilter'
import { NumberRangeFilter } from './NumberRangeFilter'
import { ReportDateRangeFilter } from './ReportDateRangeFilter'
import { ActiveFilterChips } from './ActiveFilterChips'
import { buildFilterChips } from './filterChips'

interface Props {
  readonly filters: QuoteFilters
  readonly timezone: string
  readonly onChange: (next: QuoteFilters) => void
  readonly onClearAll: () => void
}

export function QuotesFilterBar({ filters, timezone, onChange, onClearAll }: Props) {
  const [moreOpen, setMoreOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const vehicles = useAccountVehicles()
  const utm = useUtmOptions()

  const vehicleOptions = (vehicles.data ?? []).map((v) => ({ value: v.id, label: v.name }))
  const toOptions = (values: readonly string[]) => values.map((v) => ({ value: v, label: v }))

  const patch = (next: Partial<QuoteFilters>): void => {
    onChange({ ...filters, ...next })
  }

  const activeCount = countActiveFilters(filters)

  const chips = buildFilterChips(filters as QuoteFilters & never, patch as never, {
    bookingStatusOptions: [],
    paymentStatusOptions: [],
    serviceTypeOptions: QUOTE_SERVICE_TYPE_OPTIONS,
    quoteStatusOptions: QUOTE_STATUS_OPTIONS,
    vehicleOptions,
  })

  const moreFilters = (
    <div className="space-y-5">
      <section className="space-y-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Dates
        </h4>
        <div className="flex flex-col gap-2">
          <ReportDateRangeFilter
            label="Pickup date"
            value={filters.pickupDate}
            timezone={timezone}
            onChange={(pickupDate) => patch({ pickupDate })}
          />
          <ReportDateRangeFilter
            label="Created date"
            value={filters.createdDate}
            timezone={timezone}
            onChange={(createdDate) => patch({ createdDate })}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Money
        </h4>
        <NumberRangeFilter
          label="Quote total"
          currency
          min={filters.amountMin}
          max={filters.amountMax}
          onChange={({ min, max }) => patch({ amountMin: min, amountMax: max })}
        />
        <div className="space-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={filters.isOperatorPriced === true}
              onCheckedChange={(c) => patch({ isOperatorPriced: c === true ? true : undefined })}
              aria-label="Operator priced"
            />
            <span>Operator priced</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={filters.isFlatRate === true}
              onCheckedChange={(c) => patch({ isFlatRate: c === true ? true : undefined })}
              aria-label="Flat rate"
            />
            <span>Flat rate</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={filters.hasDiscount === true}
              onCheckedChange={(c) => patch({ hasDiscount: c === true ? true : undefined })}
              aria-label="Has discount"
            />
            <span>Has discount</span>
          </label>
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Trip
        </h4>
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label="Service type"
            options={QUOTE_SERVICE_TYPE_OPTIONS}
            selected={filters.serviceType ?? []}
            onChange={(v) =>
              patch({ serviceType: v.length ? (v as QuoteFilters['serviceType']) : undefined })
            }
          />
        </div>
        <NumberRangeFilter
          label="Passengers"
          min={filters.passengerMin}
          max={filters.passengerMax}
          onChange={({ min, max }) => patch({ passengerMin: min, passengerMax: max })}
        />
        <div className="space-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={filters.roundTrip === true}
              onCheckedChange={(c) => patch({ roundTrip: c === true ? true : undefined })}
              aria-label="Round trip only"
            />
            <span>Round trip only</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={filters.airportPickup === true}
              onCheckedChange={(c) => patch({ airportPickup: c === true ? true : undefined })}
              aria-label="Airport pickup"
            />
            <span>Airport pickup</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={filters.airportDropoff === true}
              onCheckedChange={(c) => patch({ airportDropoff: c === true ? true : undefined })}
              aria-label="Airport dropoff"
            />
            <span>Airport dropoff</span>
          </label>
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Attribution
        </h4>
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label="UTM source"
            options={toOptions(utm.data?.sources ?? [])}
            selected={filters.utmSource ?? []}
            onChange={(v) => patch({ utmSource: v.length ? v : undefined })}
            emptyHint="No campaign data recorded yet"
          />
          <MultiSelectFilter
            label="UTM medium"
            options={toOptions(utm.data?.mediums ?? [])}
            selected={filters.utmMedium ?? []}
            onChange={(v) => patch({ utmMedium: v.length ? v : undefined })}
            emptyHint="No campaign data recorded yet"
          />
          <MultiSelectFilter
            label="UTM campaign"
            options={toOptions(utm.data?.campaigns ?? [])}
            selected={filters.utmCampaign ?? []}
            onChange={(v) => patch({ utmCampaign: v.length ? v : undefined })}
            emptyHint="No campaign data recorded yet"
          />
        </div>
      </section>
    </div>
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, address, or quote token…"
            value={filters.search ?? ''}
            onChange={(e) => patch({ search: e.target.value || undefined })}
            className="pl-9"
            aria-label="Search quotes"
          />
        </div>

        <div className="hidden flex-wrap items-center gap-2 sm:flex">
          <ReportDateRangeFilter
            label="Pickup"
            value={filters.pickupDate}
            timezone={timezone}
            onChange={(pickupDate) => patch({ pickupDate })}
          />
          <MultiSelectFilter
            label="Status"
            options={QUOTE_STATUS_OPTIONS}
            selected={filters.status ?? []}
            onChange={(v) => patch({ status: v.length ? (v as QuoteFilters['status']) : undefined })}
          />
          <MultiSelectFilter
            label="Vehicle"
            options={vehicleOptions}
            selected={filters.vehicleIds ?? []}
            onChange={(v) => patch({ vehicleIds: v.length ? v : undefined })}
            emptyHint="No vehicles configured"
          />

          <Popover open={moreOpen} onOpenChange={setMoreOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                More filters
                {activeCount > 0 && <span className="text-xs">({activeCount})</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="max-h-[70vh] w-96 overflow-y-auto" align="end">
              {moreFilters}
            </PopoverContent>
          </Popover>
        </div>

        <div className="sm:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="w-full gap-2">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Filters
                {activeCount > 0 && <span className="text-xs">({activeCount})</span>}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-5">
                <div className="flex flex-wrap gap-2">
                  <MultiSelectFilter
                    label="Status"
                    options={QUOTE_STATUS_OPTIONS}
                    selected={filters.status ?? []}
                    onChange={(v) =>
                      patch({ status: v.length ? (v as QuoteFilters['status']) : undefined })
                    }
                  />
                  <MultiSelectFilter
                    label="Vehicle"
                    options={vehicleOptions}
                    selected={filters.vehicleIds ?? []}
                    onChange={(v) => patch({ vehicleIds: v.length ? v : undefined })}
                    emptyHint="No vehicles configured"
                  />
                </div>
                {moreFilters}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <ActiveFilterChips chips={chips} onClearAll={onClearAll} />
    </div>
  )
}
